import { useCallback, useEffect, useRef, useState } from "react";
import { VideoRecording } from "../types/video.types";
import { resolveStudyVideoStreamUrl } from "../utils/videoSyncUtils";
import logger from "@shared/utils/logger";

export type PopoutFeed = "primary" | "secondary";

export interface PopoutSyncState {
  currentTimeMs: number;
  isPlaying: boolean;
  speed: number;
  highSpeedMode: "normal" | "jump";
}

export type VideoPopoutSyncMessage =
  | {
      type: "SYNC_STATE";
      currentTimeMs: number;
      isPlaying: boolean;
      speed: number;
      highSpeedMode?: "normal" | "jump";
    }
  | {
      type: "POPOUT_SPEED_CHANGE";
      speed: number;
    }
  | {
      type: "POPOUT_PLAYBACK";
      feed: PopoutFeed;
      videoTimeMs: number;
      isPlaying: boolean;
      speed?: number;
    }
  | {
      type: "POPOUT_PIN_STATE";
      feed: PopoutFeed;
      pinned: boolean;
    }
  | {
      type: "POPOUT_REQUEST_FOCUS";
    };

interface PopoutWindows {
  primary: Window | null;
  secondary: Window | null;
}

interface PollTimers {
  primary: number | null;
  secondary: number | null;
}

export interface VideoPopoutHandlers {
  onPopoutPlayback?: (message: Extract<VideoPopoutSyncMessage, { type: "POPOUT_PLAYBACK" }>) => void;
  onPopoutSpeedChange?: (speed: number) => void;
}

const POPOUT_HEARTBEAT_MS = 500;

export function useVideoPopout(studyId: string) {
  const channelId = `video-eeg-${studyId}`;
  const channelRef = useRef<BroadcastChannel | null>(null);
  const windowsRef = useRef<PopoutWindows>({ primary: null, secondary: null });
  const pollTimersRef = useRef<PollTimers>({ primary: null, secondary: null });
  const syncStateRef = useRef<PopoutSyncState>({
    currentTimeMs: 0,
    isPlaying: false,
    speed: 1,
    highSpeedMode: "normal",
  });
  const videoSyncOffsetMsRef = useRef(0);
  const handlersRef = useRef<VideoPopoutHandlers>({});

  const [isPrimaryPopped, setIsPrimaryPopped] = useState(false);
  const [isSecondaryPopped, setIsSecondaryPopped] = useState(false);
  const [isPrimaryPinned, setIsPrimaryPinned] = useState(false);
  const [isSecondaryPinned, setIsSecondaryPinned] = useState(false);

  const hasOpenPopout = useCallback(
    () =>
      Boolean(
        (windowsRef.current.primary && !windowsRef.current.primary.closed) ||
          (windowsRef.current.secondary &&
            !windowsRef.current.secondary.closed),
      ),
    [],
  );

  const broadcast = useCallback((message: VideoPopoutSyncMessage) => {
    channelRef.current?.postMessage(message);
  }, []);

  const broadcastSyncState = useCallback(() => {
    if (!hasOpenPopout()) return;
    const state = syncStateRef.current;
    broadcast({
      type: "SYNC_STATE",
      currentTimeMs: state.currentTimeMs,
      isPlaying: state.isPlaying,
      speed: state.speed,
      highSpeedMode: state.highSpeedMode,
    });
  }, [broadcast, hasOpenPopout]);

  useEffect(() => {
    channelRef.current = new BroadcastChannel(channelId);

    channelRef.current.onmessage = (
      event: MessageEvent<VideoPopoutSyncMessage>,
    ) => {
      const message = event.data;
      if (!message?.type) return;

      if (message.type === "POPOUT_PIN_STATE") {
        if (message.feed === "primary") {
          setIsPrimaryPinned(message.pinned);
        } else {
          setIsSecondaryPinned(message.pinned);
        }
        return;
      }

      if (message.type === "POPOUT_SPEED_CHANGE") {
        handlersRef.current.onPopoutSpeedChange?.(message.speed);
        return;
      }

      if (message.type === "POPOUT_PLAYBACK") {
        handlersRef.current.onPopoutPlayback?.(message);
      }
    };

    return () => {
      channelRef.current?.close();
      channelRef.current = null;
    };
  }, [channelId]);

  useEffect(() => {
    const heartbeat = window.setInterval(() => {
      broadcastSyncState();
    }, POPOUT_HEARTBEAT_MS);

    return () => {
      window.clearInterval(heartbeat);
    };
  }, [broadcastSyncState]);

  useEffect(() => {
    if (pollTimersRef.current.primary !== null) {
      window.clearInterval(pollTimersRef.current.primary);
    }
    if (pollTimersRef.current.secondary !== null) {
      window.clearInterval(pollTimersRef.current.secondary);
    }
  }, []);

  const updateSyncState = useCallback(
    (updates: Partial<PopoutSyncState>) => {
      syncStateRef.current = {
        ...syncStateRef.current,
        ...updates,
      };
      if (hasOpenPopout()) {
        broadcastSyncState();
      }
    },
    [broadcastSyncState, hasOpenPopout],
  );

  const setVideoSyncOffsetMs = useCallback((offsetMs: number) => {
    videoSyncOffsetMsRef.current = Number.isFinite(offsetMs) ? offsetMs : 0;
  }, []);

  const getVideoSyncOffsetMs = useCallback(
    () => videoSyncOffsetMsRef.current,
    [],
  );

  const setPopoutHandlers = useCallback((handlers: VideoPopoutHandlers) => {
    handlersRef.current = handlers;
  }, []);

  const resolveVideoUrl = (video: VideoRecording): string =>
    resolveStudyVideoStreamUrl(video);

  const startPolling = useCallback((feed: PopoutFeed, win: Window) => {
    if (pollTimersRef.current[feed] !== null) {
      window.clearInterval(pollTimersRef.current[feed]!);
    }
    pollTimersRef.current[feed] = window.setInterval(() => {
      if (win.closed) {
        window.clearInterval(pollTimersRef.current[feed]!);
        pollTimersRef.current[feed] = null;
        windowsRef.current[feed] = null;
        if (feed === "primary") {
          setIsPrimaryPopped(false);
          setIsPrimaryPinned(false);
        } else {
          setIsSecondaryPopped(false);
          setIsSecondaryPinned(false);
        }
      }
    }, 500);
  }, []);

  const popout = useCallback(
    (feed: PopoutFeed, video: VideoRecording) => {
      if (windowsRef.current[feed] && !windowsRef.current[feed]!.closed) {
        windowsRef.current[feed]!.close();
      }
      if (pollTimersRef.current[feed] !== null) {
        window.clearInterval(pollTimersRef.current[feed]!);
        pollTimersRef.current[feed] = null;
      }

      const params = new URLSearchParams({
        videoUrl: resolveVideoUrl(video),
        videoId: video.video_id,
        feedLabel:
          video.feed_label ||
          (feed === "primary" ? "Primary Camera" : "Secondary Camera"),
        channelId,
        frameRate: String(video.frame_rate || 25),
        feed,
      });

      const win = window.open(
        `/eeg/video-popout?${params.toString()}`,
        `video-popout-${feed}`,
        "width=960,height=640,resizable=yes,scrollbars=no",
      );

      if (!win) {
        logger.warn("[VideoPopout] Pop-up blocked for feed:", feed);
        return;
      }

      windowsRef.current[feed] = win;
      if (feed === "primary") setIsPrimaryPopped(true);
      else setIsSecondaryPopped(true);

      startPolling(feed, win);
      window.setTimeout(() => {
        broadcastSyncState();
      }, 100);
    },
    [broadcastSyncState, channelId, startPolling],
  );

  const closePopout = useCallback((feed: PopoutFeed) => {
    if (windowsRef.current[feed] && !windowsRef.current[feed]!.closed) {
      windowsRef.current[feed]!.close();
    }
    windowsRef.current[feed] = null;
    if (pollTimersRef.current[feed] !== null) {
      window.clearInterval(pollTimersRef.current[feed]!);
      pollTimersRef.current[feed] = null;
    }
    if (feed === "primary") {
      setIsPrimaryPopped(false);
      setIsPrimaryPinned(false);
    } else {
      setIsSecondaryPopped(false);
      setIsSecondaryPinned(false);
    }
  }, []);

  const requestPinnedFocus = useCallback(() => {
    if (!hasOpenPopout()) return;
    broadcast({ type: "POPOUT_REQUEST_FOCUS" });
    windowsRef.current.primary?.focus();
    windowsRef.current.secondary?.focus();
  }, [broadcast, hasOpenPopout]);

  return {
    isPrimaryPopped,
    isSecondaryPopped,
    isPrimaryPinned,
    isSecondaryPinned,
    channelId,
    popout,
    closePopout,
    broadcast,
    updateSyncState,
    setVideoSyncOffsetMs,
    getVideoSyncOffsetMs,
    requestPinnedFocus,
    setPopoutHandlers,
  };
}

export type VideoPopoutApi = ReturnType<typeof useVideoPopout>;
